import { Dispatch, JSX, ReactNode, SetStateAction, useCallback, useEffect, useState } from 'react';

import Container from '@ui/container/container';
import { Session } from '@/types/session.types';
import { getUserTimezone } from '@ui/calendar/utils';
import useHasPermission from '@/utils/authentication/use-has-permission';
import { GENERAL_EDITABLE_PERMISSIONS } from '@/types/permissions.types';
import EditStateWrapper from '@ui/edit-state-wrapper/edit-state-wrapper';
import UnfocusedSessionInfoContent from './unfocused-session-info-content';
import FocusedSessionInfoContent from './focused-session-info-content';

import styles from './session-stream-info.module.css';

type ChildrenProps = {
  isFocusedWithin: boolean;
  setIsFocusedWithin: (isFocused: boolean) => void;
};

type TSessionStreamInfoProps = {
  children?: ReactNode | ((props: ChildrenProps) => JSX.Element);
  session?: Session;
  /** if syncProjectDates true, will update the project dates when the session dates are updated */
  syncProjectDates?: boolean;
};

const SessionStreamInfo = ({ children, session, syncProjectDates }: TSessionStreamInfoProps) => {
  const [forceOpen, setForceOpen] = useState(false);
  const [selectedDates, setSelectedDates] = useState<{
    startDate: number | null;
    endDate: number | null;
    selectedTimezone: string;
  }>({
    startDate: null,
    endDate: null,
    selectedTimezone: getUserTimezone(),
  });

  const { hasPermission: hasPermissions } = useHasPermission({
    allowedRoles: GENERAL_EDITABLE_PERMISSIONS,
  });

  // if dates have not been edited (requires_time_change) then force "open"
  const requiresTimeChange = !session?.timestamp || Object.values(session?.requires_time_change || {}).some(Boolean);
  useEffect(() => {
    setForceOpen(requiresTimeChange);
  }, [requiresTimeChange]);

  const handleSetIsFocusedWithChildrenHandler = useCallback(
    (setIsFocusedWithin: Dispatch<SetStateAction<boolean>>) => (isFocusedWithin: boolean) => {
      setIsFocusedWithin(isFocusedWithin);
      setForceOpen(isFocusedWithin);
    },
    []
  );

  return (
    <Container minHeight="108px" data-component="SessionStreamInfo">
      <EditStateWrapper
        disabled={!hasPermissions}
        Label="BASIC INFO"
        headerClassName="position-absolute width-100-percent"
        className={`${styles['session-stream-info']} position-relative`}
        keepOpen={forceOpen}
      >
        {({ isFocusedWithin, setIsFocusedWithin }) => (
          <Container
            width="100%"
            height="100%"
            minHeight={isFocusedWithin ? '108px' : '84px'}
            data-show-edit-label={isFocusedWithin ? 'false' : 'true'}
            transitionDuration="0ms"
            className={isFocusedWithin ? '' : 'margin-t-24'}
          >
            <Container
              width="100%"
              height={'84px'}
              className={`flex ai-flex-end ${styles['content-container']}`}
              hoverBackground={isFocusedWithin ? undefined : 'var(--gray-900)'}
              borderRadius="8px"
              transitionDuration={isFocusedWithin ? '0ms' : undefined}
            >
              <UnfocusedSessionInfoContent
                selectedDates={selectedDates}
                session={session}
                isFocusedWithin={isFocusedWithin}
              />

              <FocusedSessionInfoContent
                session={session}
                syncProjectDates={syncProjectDates}
                selectedDates={selectedDates}
                setSelectedDates={setSelectedDates}
                isFocusedWithin={isFocusedWithin}
              />
            </Container>

            <Container transitionDuration="0" margin={isFocusedWithin ? '32px 0 0 0' : ''}>
              {typeof children === 'function'
                ? children({
                    isFocusedWithin,
                    setIsFocusedWithin: handleSetIsFocusedWithChildrenHandler(setIsFocusedWithin),
                  })
                : children}
            </Container>
          </Container>
        )}
      </EditStateWrapper>
    </Container>
  );
};

export default SessionStreamInfo;
